import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, Check, ExternalLink, ImageUp, Lock, Trash2 } from 'lucide-react';
import { SERVICES } from '@site/data/services';
import type { ProjectInput } from '@site/shared/content';
import {
  RESERVED_IDS,
  ValidationError,
  imagePreviewUrl,
  processScreenshot,
  uploadScreenshot,
  useCaseStudies,
  useDeleteCaseStudy,
  useSaveCaseStudy,
  useTestimonials,
  type CaseStudyRow,
  type ProcessedShot,
  type TestimonialDraft,
} from '@/lib/work';
import { Page, PageHeader } from '@/components/Shell';
import { ChipInput, Field, FormSection, PairList, Switch, TextList } from '@/components/form';
import { LoadError } from '@/components/ui';

/**
 * One case study, every field the site reads. Validated on save against the
 * site's own schema (src/shared/content.ts), so a draft that saves can always
 * be built. Saving never touches the live site; Work → Publish does.
 */

const EMPTY: ProjectInput = {
  id: '',
  slug: '',
  name: '',
  industry: '',
  blurb: '',
  role: '',
  year: String(new Date().getFullYear()),
  image: '',
  stack: [],
  services: [],
  seo: { title: '', description: '' },
  story: { brief: [''], built: [{ title: '', body: '' }], decisions: [] },
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export function CaseStudyEditorScreen({ id }: { id: string }) {
  const studies = useCaseStudies();
  const testimonials = useTestimonials();
  const isNew = id === 'new';

  if (studies.isPending || testimonials.isPending) {
    return (
      <>
        <PageHeader title={isNew ? 'New case study' : 'Case study'} back={<Back />} />
        <Page>
          <div className="skeleton h-96 w-full max-w-3xl" aria-busy="true" />
        </Page>
      </>
    );
  }
  if (studies.error || testimonials.error) {
    return (
      <>
        <PageHeader title="Case study" back={<Back />} />
        <Page>
          <LoadError error={studies.error ?? testimonials.error} retry={() => studies.refetch()} />
        </Page>
      </>
    );
  }

  const row = isNew ? null : studies.data.find((s) => s.id === id) ?? null;
  if (!isNew && !row) {
    return (
      <>
        <PageHeader title="Case study" back={<Back />} />
        <Page>
          <p className="text-fg-dim">There is no case study called "{id}".</p>
        </Page>
      </>
    );
  }
  const t = row ? testimonials.data.find((x) => x.case_study_id === row.id) : undefined;
  return (
    <Editor
      key={row?.id ?? 'new'}
      row={row}
      takenIds={new Set(studies.data.map((s) => s.id))}
      takenSlugs={new Set(studies.data.filter((s) => s.id !== row?.id).flatMap((s) => [s.slug, ...s.previous_slugs]))}
      initialTestimonial={t ? { quote: t.quote, company: t.company, descriptor: t.descriptor } : null}
    />
  );
}

function Back() {
  return (
    <Link to="/work" className="btn btn-ghost btn-icon -ml-2" aria-label="Back to Work">
      <ArrowLeft className="size-5" aria-hidden />
    </Link>
  );
}

function fromRow(row: CaseStudyRow | null): ProjectInput {
  if (!row) return structuredClone(EMPTY);
  return { ...structuredClone(EMPTY), ...(row.data as object), id: row.id, slug: row.slug } as ProjectInput;
}

function Editor({
  row,
  takenIds,
  takenSlugs,
  initialTestimonial,
}: {
  row: CaseStudyRow | null;
  takenIds: Set<string>;
  takenSlugs: Set<string>;
  initialTestimonial: TestimonialDraft | null;
}) {
  const navigate = useNavigate();
  const save = useSaveCaseStudy();
  const remove = useDeleteCaseStudy();
  const isNew = !row;
  const published = Boolean(row?.first_published_at);

  const initial = useMemo(() => fromRow(row), [row]);
  const [p, setP] = useState<ProjectInput>(initial);
  const [quote, setQuote] = useState<TestimonialDraft | null>(initialTestimonial);
  const [idTouched, setIdTouched] = useState(false);
  const [slugUnlocked, setSlugUnlocked] = useState(!published);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const dirty = JSON.stringify(p) !== JSON.stringify(initial) || JSON.stringify(quote) !== JSON.stringify(initialTestimonial);

  // Leaving with unsaved edits asks first: in Desk, and when closing the tab.
  // `leaving` is set just before Desk itself navigates away after a save or a delete.
  const leaving = useRef(false);
  useBlocker({
    shouldBlockFn: () => !leaving.current && dirty && !window.confirm('Leave without saving your changes?'),
  });
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const set = <K extends keyof ProjectInput>(k: K, v: ProjectInput[K]) => setP((prev) => ({ ...prev, [k]: v }));
  const err = (k: string) => errors.get(k);

  function onName(name: string) {
    setP((prev) => {
      const next = { ...prev, name };
      if (isNew && !idTouched) {
        next.id = slugify(name);
        next.slug = slugify(name);
      }
      return next;
    });
  }

  async function onSave() {
    setBanner(null);
    const local = new Map<string, string>();
    if (isNew && takenIds.has(p.id)) local.set('id', 'Another case study already uses this id.');
    if (RESERVED_IDS.has(p.id)) local.set('id', `"${p.id}" is reserved by Desk. Pick another.`);
    if (takenSlugs.has(p.slug)) local.set('slug', 'Another case study uses (or used) this address.');
    if (!p.image) local.set('image', 'Add a screenshot of the live site.');
    if (local.size) {
      setErrors(local);
      setBanner({ ok: false, text: 'Some fields need attention before this can be saved.' });
      return;
    }
    // Empty optional fields are left out rather than saved as "".
    const project: ProjectInput = { ...p };
    for (const k of ['url', 'domain'] as const) if (!project[k]?.trim()) delete project[k];
    if (project.seo.ogImage === '') delete project.seo.ogImage;

    try {
      const savedId = await save.mutateAsync({ project, isNew, testimonial: quote });
      setErrors(new Map());
      setBanner({ ok: true, text: 'Saved. It is not on the site until you publish from Work.' });
      if (isNew) {
        leaving.current = true;
        navigate({ to: '/work/$caseStudyId', params: { caseStudyId: savedId }, replace: true });
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        setErrors(e.byPath);
        setBanner({ ok: false, text: `${e.byPath.size} ${e.byPath.size === 1 ? 'field needs' : 'fields need'} attention before this can be saved.` });
      } else {
        setBanner({ ok: false, text: e instanceof Error ? e.message : String(e) });
      }
    }
  }

  return (
    <>
      <PageHeader title={p.name || (isNew ? 'New case study' : row!.id)} back={<Back />}>
        {published && row?.visible && (
          <a href={`https://bitbew.com/work/${row.slug}`} target="_blank" rel="noreferrer" className="btn btn-ghost max-sm:hidden">
            <ExternalLink className="size-4" aria-hidden /> On the site
          </a>
        )}
      </PageHeader>
      <Page>
        <div className="grid grid-cols-1 gap-10 pb-28 xl:grid-cols-[minmax(0,1fr)_18rem]">
          <form className="grid min-w-0 gap-10" onSubmit={(e) => (e.preventDefault(), onSave())} noValidate>
            <FormSection title="The basics">
              <Field label="Client name" htmlFor="cs-name" error={err('name')}>
                <input id="cs-name" className="input" value={p.name} onChange={(e) => onName(e.target.value)} />
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Id"
                  htmlFor="cs-id"
                  error={err('id')}
                  hint={isNew ? 'How services refer to it. Fixed once saved.' : 'Fixed: services refer to it by this.'}
                >
                  <input
                    id="cs-id"
                    className="input num"
                    value={p.id}
                    disabled={!isNew}
                    onChange={(e) => {
                      setIdTouched(true);
                      set('id', slugify(e.target.value));
                    }}
                  />
                </Field>
                <SlugField
                  value={p.slug}
                  onChange={(v) => set('slug', v)}
                  error={err('slug')}
                  locked={!slugUnlocked}
                  onUnlock={() => setSlugUnlocked(true)}
                  original={row?.slug}
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-[1fr_8rem]">
                <Field label="What kind of business" htmlFor="cs-ind" error={err('industry')} hint="Two or three words: Clinical skincare.">
                  <input id="cs-ind" className="input" value={p.industry} onChange={(e) => set('industry', e.target.value)} />
                </Field>
                <Field label="Year" htmlFor="cs-year" error={err('year')}>
                  <input id="cs-year" className="input tnum" inputMode="numeric" maxLength={4} value={p.year} onChange={(e) => set('year', e.target.value.replace(/\D/g, ''))} />
                </Field>
              </div>
              <Field label="What it is, in one line" htmlFor="cs-blurb" error={err('blurb')} count={[p.blurb.length, 160]}>
                <textarea id="cs-blurb" className="input" rows={2} value={p.blurb} onChange={(e) => set('blurb', e.target.value)} />
              </Field>
              <Field label="What we were responsible for" htmlFor="cs-role" error={err('role')}>
                <textarea id="cs-role" className="input" rows={2} value={p.role} onChange={(e) => set('role', e.target.value)} />
              </Field>
            </FormSection>

            <FormSection
              title="The live site"
              lede="Leave the address empty if there is no public URL we are allowed to send people to. Never guess a domain."
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Their site" htmlFor="cs-url" error={err('url')} hint="https://silomin.com">
                  <input id="cs-url" className="input" inputMode="url" value={p.url ?? ''} onChange={(e) => set('url', e.target.value)} />
                </Field>
                <Field label="Shown as" htmlFor="cs-domain" error={err('domain')} hint="silomin.com">
                  <input id="cs-domain" className="input" value={p.domain ?? ''} onChange={(e) => set('domain', e.target.value)} />
                </Field>
              </div>
              <label className="flex items-start gap-3 text-sm">
                <Switch on={p.live !== false} label="Link out to the client's site" onChange={(on) => set('live', on ? undefined : false)} />
                <span>
                  <span className="font-semibold">Link out to their site</span>
                  <span className="block text-fg-dim">
                    Off when their site is down: every outbound link disappears, and the case study stays.
                  </span>
                </span>
              </label>
            </FormSection>

            <FormSection title="Screenshot" lede="A full-page capture of the live site. Desk keeps the top, at the shape the browser frame pans through.">
              <ScreenshotField id={p.id} image={p.image} onImage={(img) => set('image', img)} error={err('image')} />
            </FormSection>

            <FormSection title="Services and stack">
              <Field label="Services this project demonstrates" error={err('services')}>
                <div className="grid gap-2 sm:grid-cols-2">
                  {SERVICES.map((s) => (
                    <label key={s.slug} className="flex items-center gap-2.5 text-sm">
                      <input
                        type="checkbox"
                        className="size-4 accent-ink"
                        checked={p.services.includes(s.slug)}
                        onChange={(e) =>
                          set('services', e.target.checked ? [...p.services, s.slug] : p.services.filter((x) => x !== s.slug))
                        }
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Stack" htmlFor="cs-stack" error={err('stack')} hint="Press Enter after each one.">
                <ChipInput id="cs-stack" value={p.stack} onChange={(v) => set('stack', v)} placeholder="React, Supabase, Razorpay…" />
              </Field>
            </FormSection>

            <FormSection title="The brief" lede="Why the client came to us, in a paragraph or two.">
              <TextList
                value={p.story.brief}
                onChange={(brief) => set('story', { ...p.story, brief })}
                noun="paragraph"
                rows={4}
                errors={errors}
                errorKey="story.brief"
              />
              {err('story.brief') && <p className="error-text">{err('story.brief')}</p>}
            </FormSection>

            <FormSection
              title="What we built"
              lede="Every line here must be checkable on the live site. Each store is one click from anyone reading."
            >
              <PairList
                value={p.story.built}
                onChange={(built) => set('story', { ...p.story, built })}
                keys={['title', 'body']}
                labels={['Feature', 'What it does, as a visitor would see it']}
                noun="feature"
                errors={errors}
                errorKey="story.built"
              />
              {err('story.built') && <p className="error-text">{err('story.built')}</p>}
            </FormSection>

            <FormSection title="Decisions" lede="A choice we made, and the consequence for the client.">
              <PairList
                value={p.story.decisions}
                onChange={(decisions) => set('story', { ...p.story, decisions })}
                keys={['head', 'tail']}
                labels={['The decision', 'Why it mattered to them']}
                noun="decision"
                errors={errors}
                errorKey="story.decisions"
              />
            </FormSection>

            <FormSection
              title="Testimonial"
              lede="Only with the client's approval. Attributed to the business, never a named person, and never a figure we did not measure."
            >
              {quote ? (
                <div className="grid gap-4">
                  <Field label="Their words" htmlFor="t-quote" error={err('testimonial.quote')}>
                    <textarea id="t-quote" className="input" rows={4} value={quote.quote} onChange={(e) => setQuote({ ...quote, quote: e.target.value })} />
                  </Field>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Business" htmlFor="t-company" error={err('testimonial.company')}>
                      <input id="t-company" className="input" value={quote.company} onChange={(e) => setQuote({ ...quote, company: e.target.value })} />
                    </Field>
                    <Field label="What the business is" htmlFor="t-desc" error={err('testimonial.descriptor')} hint="Clinical skincare brand">
                      <input id="t-desc" className="input" value={quote.descriptor} onChange={(e) => setQuote({ ...quote, descriptor: e.target.value })} />
                    </Field>
                  </div>
                  <div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuote(null)}>
                      Remove the testimonial
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    onClick={() => setQuote({ quote: '', company: p.name, descriptor: p.industry })}
                  >
                    Add an approved testimonial
                  </button>
                </div>
              )}
            </FormSection>

            <FormSection title="Search result">
              <Field label="Title" htmlFor="seo-title" error={err('seo.title')} count={[p.seo.title.length, 60]} hint="Unique, keyword first: “Client: what it is Case Study | Bitbew”.">
                <input id="seo-title" className="input" value={p.seo.title} onChange={(e) => set('seo', { ...p.seo, title: e.target.value })} />
              </Field>
              <Field label="Description" htmlFor="seo-desc" error={err('seo.description')} count={[p.seo.description.length, 155]} hint="Written for a person deciding whether to click.">
                <textarea id="seo-desc" className="input" rows={3} value={p.seo.description} onChange={(e) => set('seo', { ...p.seo, description: e.target.value })} />
              </Field>
              <SerpPreview title={p.seo.title} description={p.seo.description} slug={p.slug} />
            </FormSection>

            {isNew ? null : !published ? (
              <section className="border-t border-edge pt-8">
                <button
                  type="button"
                  className="btn btn-ghost text-alert"
                  disabled={remove.isPending}
                  onClick={async () => {
                    if (!window.confirm(`Delete ${p.name || row!.id}? It has never been on the site, so nothing else changes.`)) return;
                    await remove.mutateAsync(row!);
                    leaving.current = true;
                    navigate({ to: '/work' });
                  }}
                >
                  <Trash2 className="size-4" aria-hidden /> Delete this draft
                </button>
              </section>
            ) : null}
          </form>

          <aside className="hidden xl:block">
            <div className="sticky top-24 grid gap-4 text-sm">
              <Rules />
            </div>
          </aside>
        </div>
      </Page>

      <div className="fixed inset-x-0 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-20 border-t border-edge bg-paper lg:bottom-0 lg:left-60">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <p
            className={`min-w-0 flex-1 truncate text-sm ${banner ? (banner.ok ? 'text-ok' : 'text-alert') : 'text-fg-dim'}`}
            role={banner && !banner.ok ? 'alert' : 'status'}
          >
            {banner?.text ?? (dirty ? 'Unsaved changes.' : published ? 'Saved. Publish from Work to update the site.' : 'Not on the site yet.')}
          </p>
          <button type="button" className="btn btn-primary" disabled={save.isPending || !dirty} onClick={onSave}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- the URL */

function SlugField({
  value,
  onChange,
  error,
  locked,
  onUnlock,
  original,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
  locked: boolean;
  onUnlock: () => void;
  original?: string;
}) {
  const changed = original && value !== original;
  return (
    <Field
      label="Page on bitbew.com"
      htmlFor="cs-slug"
      error={error}
      hint={
        changed ? (
          <span className="text-warn">
            After you publish, /work/{original} forwards permanently to /work/{value}.
          </span>
        ) : locked ? (
          'Published: people may have this link.'
        ) : (
          'bitbew.com/work/…'
        )
      }
    >
      <div className="flex gap-2">
        <input
          id="cs-slug"
          className="input num"
          value={value}
          disabled={locked}
          onChange={(e) => onChange(slugify(e.target.value))}
        />
        {locked && (
          <button
            type="button"
            className="btn btn-quiet shrink-0"
            onClick={() => {
              if (window.confirm('This address is public. Change it? The old one will forward to the new one after you publish.')) onUnlock();
            }}
          >
            <Lock className="size-4" aria-hidden /> Change
          </button>
        )}
      </div>
    </Field>
  );
}

/* ------------------------------------------------------------- screenshot */

function ScreenshotField({
  id,
  image,
  onImage,
  error,
}: {
  id: string;
  image: string;
  onImage: (path: string) => void;
  error?: string;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [quality, setQuality] = useState(78);
  const [shot, setShot] = useState<ProcessedShot | null>(null);
  const [state, setState] = useState<'idle' | 'working' | 'uploading'>('idle');
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setState('working');
    const t = window.setTimeout(async () => {
      try {
        const next = await processScreenshot(file, quality);
        if (cancelled) return URL.revokeObjectURL(next.previewUrl);
        setShot((old) => {
          if (old) URL.revokeObjectURL(old.previewUrl);
          return next;
        });
        setProblem(null);
      } catch (e) {
        setProblem(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setState('idle');
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [file, quality]);

  const kb = (b: Blob) => `${Math.round(b.size / 1024)} KB`;

  return (
    <div className="grid gap-4">
      {image && !shot && (
        <div className="overflow-hidden rounded-lg border border-edge bg-bg-sunk">
          <img src={imagePreviewUrl(image)} alt="The current screenshot" className="block max-h-80 w-full object-cover object-top" />
        </div>
      )}
      {shot && (
        <div className="overflow-hidden rounded-lg border border-edge-strong bg-bg-sunk">
          <img src={shot.previewUrl} alt="The new screenshot, as the site will show it" className="block max-h-80 w-full object-cover object-top" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <label className="btn btn-quiet cursor-pointer">
          <ImageUp className="size-4" aria-hidden /> {image ? 'Replace screenshot' : 'Choose screenshot'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
        </label>
        {image && <span className="num text-xs text-fg-dim">{image}</span>}
      </div>

      {file && (
        <div className="grid gap-3 rounded-lg border border-edge bg-bg-raised p-4">
          <div className="field">
            <div className="flex items-baseline justify-between">
              <label htmlFor="shot-q" className="label">
                Quality
              </label>
              <span className="tnum text-sm">
                {quality}
                {shot && (
                  <span className="text-fg-dim">
                    {' '}
                    · WebP {kb(shot.webp)} · JPEG {kb(shot.jpg)}
                  </span>
                )}
              </span>
            </div>
            <input
              id="shot-q"
              type="range"
              min={55}
              max={90}
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              className="w-full accent-ink"
            />
            <p className="hint">78 suits flat marketing pages. Photo-heavy sites look the same at 65–70 for a third of the size.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn btn-ink btn-sm"
              disabled={!shot || state !== 'idle' || !id}
              onClick={async () => {
                if (!shot) return;
                setState('uploading');
                try {
                  onImage(await uploadScreenshot(id, shot));
                  setFile(null);
                  setShot(null);
                } catch (e) {
                  setProblem(e instanceof Error ? e.message : String(e));
                } finally {
                  setState('idle');
                }
              }}
            >
              {state === 'uploading' ? 'Uploading…' : state === 'working' ? 'Processing…' : 'Use this screenshot'}
            </button>
            {!id && <span className="hint">Give the case study a name first.</span>}
          </div>
        </div>
      )}
      {(problem || error) && <p className="error-text">{problem ?? error}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------- previews */

function SerpPreview({ title, description, slug }: { title: string; description: string; slug: string }) {
  const cut = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
  return (
    <div className="rounded-lg border border-edge bg-white p-4" aria-label="How it may look in Google">
      <p className="text-xs text-fg-dim">bitbew.com › work › {slug || '…'}</p>
      <p className="mt-0.5 text-lg leading-snug text-[#1a0dab]">{cut(title || 'Title', 60)}</p>
      <p className="mt-1 text-sm text-fg-dim">{cut(description || 'Description', 158)}</p>
    </div>
  );
}

function Rules(): ReactNode {
  return (
    <div className="rounded-lg border border-edge bg-bg-raised p-4">
      <p className="flex items-center gap-2 font-semibold">
        <AlertTriangle className="size-4 text-warn" aria-hidden /> Before saving
      </p>
      <ul className="mt-3 grid gap-2.5 text-fg-dim">
        <li className="flex gap-2"><Check className="mt-0.5 size-3.5 shrink-0" aria-hidden />Every feature is checkable on the live site today.</li>
        <li className="flex gap-2"><Check className="mt-0.5 size-3.5 shrink-0" aria-hidden />No numbers we did not measure, and none in a testimonial.</li>
        <li className="flex gap-2"><Check className="mt-0.5 size-3.5 shrink-0" aria-hidden />No invented domain: leave the address empty instead.</li>
        <li className="flex gap-2"><Check className="mt-0.5 size-3.5 shrink-0" aria-hidden />The site never says how many projects there are.</li>
        <li className="flex gap-2"><Check className="mt-0.5 size-3.5 shrink-0" aria-hidden />Screenshots are never tinted into the house colours.</li>
      </ul>
    </div>
  );
}
